import type { ReactNode } from 'react';

interface Props {
  /** The primary control row (search, quick chips, Add filter, sort). */
  children: ReactNode;
  /** Optional active-filter summary row (see ActiveFilters) — rendered below a divider. */
  active?: ReactNode;
  className?: string;
  /**
   * Phone ergonomics: below `sm` the control row becomes ONE horizontally
   * swipeable strip (edge-bled to the screen) instead of wrapping into a
   * stack of chip rows that pushes the content down. From `sm` up the row
   * wraps exactly as before — desktop rendering is unchanged. Opt-in so
   * existing consumers keep their current behavior.
   */
  mobileScroll?: boolean;
}

/**
 * The shared toolbar shell: the primary control row plus an optional
 * active-filter row. Deliberately chromeless — a quiet wrap-row of compact
 * controls rather than a bordered/shadowed card, so filters read as page
 * chrome instead of content. Replaces the hand-rolled toolbar containers
 * that had drifted across the modules.
 */
export default function FilterBar({ children, active, className = '', mobileScroll = false }: Props) {
  return (
    <div className={className}>
      <div
        className={
          mobileScroll
            ? 'flex items-center gap-2 overflow-x-auto pb-1 -mx-4 px-4 [-webkit-overflow-scrolling:touch] sm:mx-0 sm:flex-wrap sm:overflow-x-visible sm:px-0 sm:pb-0'
            : 'flex flex-wrap items-center gap-2'
        }
      >
        {children}
      </div>
      {active}
    </div>
  );
}
